import {
  Component,
  ViewChild,
  QueryList,
  Injectable,
  Input,
} from '@angular/core'
import {
  CanDeactivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router'
import moment from 'moment'
import pluralize from 'pluralize'
import { BaseComponent } from 'components/base.component'
import get from 'lodash-es/get'
import { customerInContext } from 'logic/customer-states'
import { CustomerState } from 'types'
import { daysUntilDate, fullDayNoYear } from 'utils'
import { QueuedSubmissionsListComponent } from '../queued-submissions-list/queued-submissions-list.component'
import sweetAlert from 'bmAlert'
import {
  SubmissionQueries,
  Customer,
  Data,
  User,
  Post,
  Submission,
} from '../../submission.queries'
import { QueryRef } from 'apollo-angular'
import {
  VideoBoxActionEvent,
  VideoBoxPurchaseEvent,
  VideoBoxCommentEvent,
} from 'src/modules/video-box/video-box.component'
import { SubmissionMutations } from '../../submission.mutations'
import { successToast } from 'src/toaster'
import { submissionInContext } from 'src/logic/submission-states'

pluralize.addIrregularRule('it', 'they')

@Component({
  selector: 'queued-submissions-page',
  styleUrls: ['queued-submissions-page.scss'],
  templateUrl: './queued-submissions-page.html',
})
export class QueuedSubmissionsPage extends BaseComponent {
  @ViewChild(QueuedSubmissionsListComponent)
  submissionsList: QueuedSubmissionsListComponent
  @Input() customer: Customer
  @Input() customerId: number
  @Input() submissionsLoading: boolean
  @Input() queryRef: QueryRef<Data>
  @Input() posts: Post[] = []
  @Input() isSearch: boolean
  @Input() submissions: Submission[] = []
  @Input() currentUser: User
  modalVisible: boolean = false
  showPendingRequestsWarning: boolean = false

  constructor(
    private submissionQueries: SubmissionQueries,
    private submissionMutations: SubmissionMutations
  ) {
    super()
    this.clearIntervals()
    const interval = setInterval(() => {
      if (navigator.onLine) this.queryRef.refetch()
    }, 900000) // 15 min
    this.intervals.push(interval)
  }

  get agencyName(): string {
    return this.currentUser?.account?.accountName || ''
  }

  onDeprecatedStoreChange(postId: number): void {
    // TODO: If there is a postId, just refetch that one post (small toggling updates)
    // Otherwise, refetch the whole query (moving posts between lists, changing counts, etc...)
    this.queryRef.refetch()
  }

  onAction({ submission, action }: VideoBoxActionEvent): void {
    if (submission.state === 'new') {
      this.submissionMutations.updateSubmission({
        submission,
        updates: { state: 'viewed' },
      })
    }
    switch (action) {
      case 'view':
        const expanded = !submission.expanded
        this.submissionMutations.updateSubmissionUi({
          submission,
          updates: { expanded, visibleForm: null },
        })
        break
      case 'accept':
      case 'requestEdits':
        if (submissionInContext(submission, 'purchaseable'))
          this.submissionMutations.updateSubmissionUi({
            submission,
            updates: {
              expanded: true,
              visibleForm: submission.visibleForm === action ? null : action,
            },
          })
        break
      case 'viewAndPlay':
        this.submissionMutations
          .updateSubmissionUi({ submission, updates: { expanded: true } })
          .then(() => {
            const videoBox = this.submissionsList.videoBoxes.find(
              (videoBox) => videoBox.submission.id === submission.id
            )
            if (videoBox) videoBox.playVideo()
          })
        break
      default:
        break
    }
  }

  onAccept({ submission }: VideoBoxPurchaseEvent): void {
    this.submissionMutations.purchaseSubmission({ submission }).then(() => {
      this.submissionMutations.updateSubmissionUi({
        submission,
        updates: { expanded: false, visibleForm: null },
      })
      successToast('Success! Your video has been delivered.')
    })
  }

  onRequestEdits({ submission, comments }: VideoBoxCommentEvent): void {
    this.submissionMutations
      .updateSubmission({
        submission,
        updates: { comments, state: 'awaiting_editing' },
      })
      .then(() => {
        this.submissionMutations.updateSubmissionUi({
          submission,
          updates: { expanded: false, visibleForm: null },
        })
        successToast('Editing Requested')
      })
  }

  customerInContext(context: CustomerState | 'current'): boolean {
    return this.customer && customerInContext(this.customer, context)
  }

  nextPostingDateTime(): Date {
    return new Date(this.customer.nextPostingTime)
  }

  daysUntilDate(date: Date): number {
    return daysUntilDate(date)
  }

  formatted(date: Date): string {
    return fullDayNoYear(date)
  }

  flash(): { class: string; msg: string } {
    const expiringCreditsMsg =
      this.currentUser &&
      this.currentUser.canUpdateSubscription &&
      this.expiringCreditsMessage()
    if (expiringCreditsMsg) {
      return {
        msg: expiringCreditsMsg,
        class: 'alert alert-info',
      }
    }
  }

  expiringCreditsMessage(): string {
    if (this.customer?.multipass) return this.multipassExpiringCreditsMessage()
    return this.legacyExpiringCreditsMessage()
  }

  private legacyExpiringCreditsMessage(): string {
    const c = this.customer
    if (c && c.subscription && this.customerInContext('current')) {
      const nextCreditsExpireAt = moment(c.subscription.nextCreditsExpireAt)
      const expiringCredits = c.subscription.nextExpiringCreditsCount
      if (
        expiringCredits > 0 &&
        nextCreditsExpireAt.isBefore(moment().add(1, 'month'))
      ) {
        let msg = `${expiringCredits} ${pluralize(
          'credit',
          expiringCredits
        )} ${pluralize('is', expiringCredits)} almost six months old.`
        const expiresString = nextCreditsExpireAt.format('dddd, MMMM Do')
        if (c.queuedPostsCount === 0) {
          msg += ` ${pluralize(
            'It',
            expiringCredits
          )} will expire on ${expiresString} if no action is taken.`
        } else if (expiringCredits <= c.queuedPostsCount) {
          msg += ` ${expiringCredits} ${pluralize(
            'post',
            expiringCredits
          )} will be sent to you on ${expiresString} if no action is taken.`
        } else {
          const difference = expiringCredits - c.queuedPostsCount
          msg += ` ${c.queuedPostsCount} ${pluralize(
            'post',
            c.queuedPostsCount
          )} will be sent to you and ${difference} ${pluralize(
            'credit',
            difference
          )} will expire on ${expiresString} if no action is taken.`
        }
        msg +=
          ' <a href="https://help.verblio.com/billing-and-invoices/credit-expiration-policy" target="_blank">Learn more</a>.'
        return msg
      }
    }
  }

  private multipassExpiringCreditsMessage(): string {
    if (
      this.customer &&
      this.customer?.subscription &&
      this.customerInContext('current') &&
      moment(this.customer.subscription.nextCreditsExpireAt).isBefore(
        moment().add(1, 'month')
      ) &&
      this.customer.subscription.nextExpiringCreditsCount > 0
    ) {
      const nextCreditsExpireAt = moment(
        this.customer.subscription.nextCreditsExpireAt
      )
      const expiringCredits =
        this.customer.subscription.nextExpiringCreditsCount
      const message = `${expiringCredits} ${pluralize(
        'credit',
        expiringCredits
      )} credits will expire on ${nextCreditsExpireAt.format(
        'dddd, MMMM Do'
      )}. Activate a request or purchase queued submissions to use these credits.  <a href="https://help.verblio.com/billing-and-invoices/credit-expiration-policy" target="_blank">Learn more</a>.`
      return message
    }
  }

  get showSubmissionList(): boolean {
    return !this.submissionsLoading && this.customerInContext('current')
  }

  get showEmptyStateCustomerPending(): boolean {
    return !this.submissionsLoading && this.customerInContext('pending')
  }

  get showEmptyStateCustomerExpired(): boolean {
    return !this.submissionsLoading && this.customerInContext('expired')
  }

  get showEmptyWhiteLabelNoSubscriptions(): boolean {
    return (
      !this.submissionsLoading &&
      this.currentUser?.role === 'white_label' &&
      !this.customer
    )
  }
}

@Injectable()
export class CanDeactivatePostsPage implements CanDeactivate<any> {
  canDeactivate(
    component: any,
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> | boolean {
    const pbs = get(
      component.postList || component,
      'postBoxes',
      new QueryList()
    ).toArray()
    let showMessage = false
    let postBox
    for (let i = 0; i < pbs.length; i++) {
      postBox = pbs[i]
      if (postBox.dirty) {
        showMessage = true
        break
      }
    }

    if (showMessage) {
      const confirmationMessage =
        'Are you sure? All unsaved changes will be lost.'
      return sweetAlert.confirm({ text: confirmationMessage })
    }
    return true
  }
}
